import React, { useEffect, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { getSiteAdminTemplateCategoriesRoute, updateTemplateCategoryRoute } from '../../../api/routes';
import { ReactComponent as PencilIcon } from '../../../assets/icons/Pencil.svg';
import config from '../../../config';
import '../SiteAdmin.scss';

// ── Toggle component ───────────────────────────────────────────────────────────

const FlagToggle = ({ label, value, onChange, saving }) => (
    <div className="tm-flag" onClick={() => !saving && onChange(!value)}>
        <div className={`tm-flag-toggle${value ? ' on' : ''}`} />
        <span>{label}</span>
    </div>
);

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

// ── Category row ───────────────────────────────────────────────────────────────

const CategoryRow = ({ category, onFlagChange, onEdit }) => {
    const [saving, setSaving] = useState(false);

    const handleFlag = async (flag, value) => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${updateTemplateCategoryRoute}/${category.id}`,
                { [flag]: value },
                { withCredentials: true }
            );
            if (res.data.success) {
                onFlagChange(category.id, { [flag]: value });
            }
        } catch {
            toast.error('Failed to update category');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="template-manager-row">
            <div
                className="tm-thumb"
                style={
                    category.categoryImage
                        ? { backgroundImage: `url(${toDisplayUrl(category.categoryImage)})` }
                        : {}
                }
            />
            <div className="tm-info">
                <div className="tm-name">{category.name}</div>
                <div className="tm-type">
                    <span
                        className={`tcm-status-badge${category.isActive ? ' active' : ' inactive'}`}
                    >
                        {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>
            <div className="tm-desc">
                <span className="tm-desc-text">{category.description || ''}</span>
            </div>
            <div className="tm-flags">
                <FlagToggle
                    label="Active"
                    value={!!category.isActive}
                    saving={saving}
                    onChange={v => handleFlag('isActive', v)}
                />
            </div>
            <div className="tm-edit-icon" onClick={() => onEdit(category)} title="Edit category">
                <PencilIcon className="tm-pencil" aria-label="Edit" />
            </div>
        </div>
    );
};

// ── Main TemplateCategoryManager ───────────────────────────────────────────────

const TemplateCategoryManager = ({ onEdit, onCreate, categoryPatch }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');

    useEffect(() => {
        if (categoryPatch) {
            setCategories(prev =>
                prev.map(c => c.id === categoryPatch.id ? { ...c, ...categoryPatch } : c)
            );
        }
    }, [categoryPatch]);

    useEffect(() => {
        axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) setCategories(res.data.categories);
            })
            .catch(() => toast.error('Failed to load template categories'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFlagChange = (id, patch) => {
        setCategories(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    };

    let filtered = search
        ? categories.filter(c =>
            c.name?.toLowerCase().includes(search.toLowerCase()) ||
            c.description?.toLowerCase().includes(search.toLowerCase())
          )
        : categories;
    if (filterActive === 'active') filtered = filtered.filter(c => c.isActive === true);
    if (filterActive === 'inactive') filtered = filtered.filter(c => c.isActive !== true);

    if (loading) return <div className="home-loading-placeholder" />;

    return (
        <div>
            {onCreate && (
                <div style={{ marginBottom: '1rem', width: '180px' }}>
                    <div className="standard-button button-active" onClick={onCreate}>
                        + Create Category
                    </div>
                </div>
            )}
            <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <input
                    className="sa-text-input"
                    style={{ maxWidth: '320px' }}
                    placeholder="Search categories…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    className="sa-select sa-select--narrow"
                    value={filterActive}
                    onChange={e => setFilterActive(e.target.value)}
                >
                    <option value="all">All Statuses</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                </select>
                <span style={{ fontSize: '0.95rem', color: 'var(--label-color)' }}>
                    {filtered.length} categor{filtered.length !== 1 ? 'ies' : 'y'}
                    {filtered.length !== categories.length && (
                        <>{' · '}out of {categories.length} total</>
                    )}
                </span>
            </div>

            <div className="template-manager-grid">
                {filtered.map(c => (
                    <CategoryRow key={c.id} category={c} onFlagChange={handleFlagChange} onEdit={onEdit} />
                ))}
                {filtered.length === 0 && (
                    <p style={{ color: 'var(--label-color)', fontSize: '1rem' }}>
                        No categories found.
                    </p>
                )}
            </div>
        </div>
    );
};

export default TemplateCategoryManager;
